import type { Permisos } from '../api/capacidades'
import { type Interruptores, TODO_ENCENDIDO } from '../api/interruptores'
import type { Sitio } from '../api/tipos'
import { esDeAgencia } from '../marca/marca'

/**
 * Qué ve cada quien en el menú. Es una función pura y se prueba con una tabla: lo que
 * el cliente **no** tiene contratado no aparece, y lo que sus capacidades no le dejan
 * tocar, tampoco.
 *
 * Esconder no es proteger (§7 de CLAUDE.md): lo que de verdad cierra la puerta es el
 * `access` del núcleo, con las mismas capacidades (`acceso/capacidades.ts`). Esto es
 * para que el panel no enseñe puertas que no abren. Mientras las capacidades se están
 * leyendo (`permisos.cargando`) el menú es el mínimo: ninguna puerta, y ningún error.
 *
 * El icono es un nombre; quién lo pinta es el menú (`Marco.tsx`), para que esto siga
 * sin saber de React.
 */
export type Icono =
  | 'agenda'
  | 'ajustes'
  | 'bloqueos'
  | 'clientes-de-agenda'
  | 'recursos'
  | 'reservas'
  | 'auditoria'
  | 'campos'
  | 'taxonomias'
  | 'tipo'
  | 'tipos-de-contenido'
  | 'categorias'
  | 'categorias-de-servicios'
  | 'creditos'
  | 'dominio'
  | 'idiomas'
  | 'entradas'
  | 'entregas'
  | 'etiquetas'
  | 'formularios'
  | 'generar'
  | 'medios'
  | 'menus'
  | 'no-encontradas'
  | 'google'
  | 'paginas'
  | 'pagos'
  | 'pedidos'
  | 'productos'
  | 'redirecciones'
  | 'respuestas'
  | 'servicios'
  | 'sitios'
  | 'socios'
  | 'usuarios'
  | 'versiones'
  | 'visitas'
  | 'revision'
  | 'salud'
  | 'clientes-agencia'
  | 'planes-agencia'

/** `variante`: con `icono: 'tipo'`, el icono que eligió el tipo de contenido (`casa`, `libro`…); el menú lo traduce. */
export type Enlace = { etiqueta: string; href: string; icono: Icono; variante?: string }

/** Lo que el menú necesita de cada tipo de contenido activo de la web: una entrada en «Contenido». */
export type TipoDelMenu = { id: number; nombrePlural: string; icono?: null | string }
export type Seccion = { etiqueta: string; enlaces: Enlace[] }

/** `agencia`: si este espacio es la cuenta propia de una agencia revendedora (§36), y qué lleva quien mira. */
export const seccionesDelEspacio = ({ base, permisos, agencia, pagos = false }: { agencia?: null | { clientes: boolean; cobros?: boolean; marca?: boolean; planes: boolean }; base: string; pagos?: boolean; permisos: Permisos }): Seccion[] => {
  const enlaces: Enlace[] = [{ etiqueta: 'Mis sitios web', href: base, icono: 'sitios' }]
  if (permisos.cargando) return [{ etiqueta: 'Espacio de trabajo', enlaces }]
  const { tiene } = permisos
  if (tiene('usuarios.invitar') || tiene('usuarios.editar')) enlaces.push({ etiqueta: 'Usuarios', href: `${base}/usuarios`, icono: 'usuarios' })
  if (tiene('creditos.ver')) enlaces.push({ etiqueta: 'Créditos y plan', href: `${base}/creditos`, icono: 'creditos' })
  // El cliente de una agencia revendedora paga su plan a la agencia (§36, R5): a quién, cómo y sus recibos.
  if (pagos && tiene('creditos.ver')) enlaces.push({ etiqueta: 'Pagos', href: `${base}/pagos`, icono: 'pagos' })
  // Todo cliente es socio sin alta (§30); lo que es de la cuenta —y su dinero— es del dueño.
  // Los clientes de una agencia revendedora (§36) no entran en el programa de socios de Wixy.
  if (permisos.esDueno && !esDeAgencia()) enlaces.push({ etiqueta: 'Recomienda y gana', href: `${base}/socios`, icono: 'socios' })
  if (tiene('auditoria.ver')) enlaces.push({ etiqueta: 'Auditoría', href: `${base}/auditoria`, icono: 'auditoria' })
  const deLaAgencia: Enlace[] = []
  if (agencia?.clientes) deLaAgencia.push({ etiqueta: 'Mis clientes', href: `${base}/agencia/clientes`, icono: 'clientes-agencia' })
  if (agencia?.planes) deLaAgencia.push({ etiqueta: 'Mis planes', href: `${base}/agencia/planes`, icono: 'planes-agencia' })
  if (agencia?.cobros) deLaAgencia.push({ etiqueta: 'Cobros', href: `${base}/agencia/cobros`, icono: 'pagos' })
  if (agencia?.marca) deLaAgencia.push({ etiqueta: 'Mi marca y dominios', href: `${base}/agencia/marca`, icono: 'dominio' })
  return [{ etiqueta: 'Espacio de trabajo', enlaces }, ...(deLaAgencia.length ? [{ etiqueta: 'Agencia', enlaces: deLaAgencia }] : [])]
}

export const seccionesDelSitio = ({ base, permisos, sitio, tipos = [], interruptores = TODO_ENCENDIDO }: { base: string; interruptores?: Interruptores; permisos: Permisos; sitio: Sitio; tipos?: TipoDelMenu[] }): Seccion[] => {
  if (permisos.cargando) return []
  const modulos = sitio.modulos ?? []
  const tiene = (modulo: string) => modulos.includes(modulo)
  const puede = permisos.tiene
  const secciones: Seccion[] = []

  // Las visitas de la web (§34): lo primero que mira quien quiere saber si su web funciona.
  if (puede('analitica.ver')) secciones.push({ etiqueta: 'Resumen', enlaces: [{ etiqueta: 'Visitas', href: `${base}/visitas`, icono: 'visitas' }] })

  const contenido: Enlace[] = []
  // Quien solo edita lo suyo (el autor) no escribe páginas (§7), así que no se le ofrecen: un
  // listado que solo deja mirar es una puerta que no abre. Medios, a quien los ve: elige imágenes.
  if (tiene('paginas') && puede('contenido.editarAjeno')) contenido.push({ etiqueta: 'Páginas', href: `${base}/paginas`, icono: 'paginas' })
  // Una entrada por cada tipo de contenido activo de la web, con su nombre en plural («Propiedades»): plan del CMS dinámico §6.
  if (tiene('estructura') && puede('contenido.ver')) {
    for (const tipo of tipos) contenido.push({ etiqueta: tipo.nombrePlural, href: `${base}/contenidos/${tipo.id}`, icono: 'tipo', variante: tipo.icono ?? undefined })
  }
  if (puede('medios.ver')) contenido.push({ etiqueta: 'Medios', href: `${base}/medios`, icono: 'medios' })
  if (puede('menus.editar')) contenido.push({ etiqueta: 'Menús', href: `${base}/menus`, icono: 'menus' })
  if (contenido.length) secciones.push({ etiqueta: 'Contenido', enlaces: contenido })

  // Las categorías son del blog y de la tienda (§6): con blog van en «Blog»; sin él, en «Tienda».
  const categorias: Enlace = { etiqueta: 'Categorías', href: `${base}/categorias`, icono: 'categorias' }
  if (tiene('entradas') && puede('contenido.ver')) {
    const blog: Enlace[] = [{ etiqueta: 'Entradas', href: `${base}/entradas`, icono: 'entradas' }]
    if (puede('terminos.editar')) blog.push(categorias)
    // Las etiquetas se crean mientras se escribe (§7): quien ve el contenido ve su lista.
    blog.push({ etiqueta: 'Etiquetas', href: `${base}/etiquetas`, icono: 'etiquetas' })
    secciones.push({ etiqueta: 'Blog', enlaces: blog })
  }

  if (tiene('productos')) {
    const tienda: Enlace[] = []
    // El catálogo es contenido: el núcleo lo protege con `contenido.*`, y quien solo edita lo suyo no lo administra.
    if (puede('contenido.editarAjeno')) tienda.push({ etiqueta: 'Productos', href: `${base}/productos`, icono: 'productos' })
    if (!tiene('entradas') && puede('terminos.editar')) tienda.push(categorias)
    if (puede('tienda.pedidos')) tienda.push({ etiqueta: 'Pedidos', href: `${base}/pedidos`, icono: 'pedidos' })
    // Las zonas de envío, el recojo y Google Maps son configuración de la tienda, como su pasarela.
    if (puede('tienda.envios')) tienda.push({ etiqueta: 'Envíos y entregas', href: `${base}/entregas`, icono: 'entregas' })
    if (puede('tienda.pasarelas')) tienda.push({ etiqueta: 'Pasarela de pago', href: `${base}/pagos`, icono: 'pagos' })
    if (tienda.length) secciones.push({ etiqueta: 'Tienda', enlaces: tienda })
  }

  // El catálogo de servicios, con sus propias categorías (no son las de la tienda ni las del blog).
  if (tiene('servicios') && puede('contenido.editarAjeno')) {
    secciones.push({
      etiqueta: 'Servicios',
      enlaces: [
        { etiqueta: 'Servicios', href: `${base}/servicios`, icono: 'servicios' },
        { etiqueta: 'Categorías de servicios', href: `${base}/categorias-de-servicios`, icono: 'categorias-de-servicios' },
      ],
    })
  }

  // La agenda de reservas (§33): con el módulo, lo de cada día a quien la ve; la configuración y la
  // activación, a quien configura la cuenta. Sin el módulo, la puerta para activarla (si el plan no la
  // incluye, la pantalla lo dice y lleva a «Créditos y plan»).
  if (tiene('agenda') && puede('agenda.ver')) {
    const agenda: Enlace[] = [
      { etiqueta: 'Reservas', href: `${base}/reservas`, icono: 'reservas' },
      { etiqueta: 'Clientes de la agenda', href: `${base}/clientes-de-agenda`, icono: 'clientes-de-agenda' },
      { etiqueta: 'Recursos', href: `${base}/recursos`, icono: 'recursos' },
      { etiqueta: 'Días y horas cerrados', href: `${base}/bloqueos`, icono: 'bloqueos' },
    ]
    if (puede('agenda.configurar')) agenda.push({ etiqueta: 'Ajustes de la agenda', href: `${base}/agenda`, icono: 'agenda' })
    secciones.push({ etiqueta: 'Agenda', enlaces: agenda })
  } else if (!tiene('agenda') && puede('agenda.configurar')) {
    secciones.push({ etiqueta: 'Agenda', enlaces: [{ etiqueta: 'Activar reservas', href: `${base}/agenda`, icono: 'agenda' }] })
  }

  // Lo que llega por un formulario son «Respuestas» (30-09-2026): «Envíos» se confundía con los
  // envíos de la tienda. Por dentro la colección sigue siendo `envios`.
  if (tiene('formularios')) {
    const formularios: Enlace[] = []
    if (puede('formularios.editar')) formularios.push({ etiqueta: 'Formularios', href: `${base}/formularios`, icono: 'formularios' })
    if (puede('respuestas.ver')) formularios.push({ etiqueta: 'Respuestas', href: `${base}/respuestas`, icono: 'respuestas' })
    if (formularios.length) secciones.push({ etiqueta: 'Formularios', enlaces: formularios })
  }

  // Las redirecciones y los 404 son del SEO de toda la web (`seo.sitio`); quien edita el SEO de
  // cada documento los mira, y convierte un 404 en redirección desde su ficha (Modulo.tsx).
  if (puede('seo.sitio') || puede('seo.editar')) {
    secciones.push({
      etiqueta: 'SEO',
      enlaces: [
        // Lo que la web entera tiene que arreglar: enlaces rotos, huérfanas, puntuación (src/seo/salud.ts).
        { etiqueta: 'Salud del sitio', href: `${base}/salud`, icono: 'salud' },
        // La revisión semanal con IA (§35): propuestas de título y descripción para las páginas más flojas.
        ...(puede('seo.editar') ? [{ etiqueta: 'Revisión semanal', href: `${base}/revision`, icono: 'revision' as const }] : []),
        { etiqueta: 'Redirecciones', href: `${base}/redirecciones`, icono: 'redirecciones' },
        { etiqueta: 'Páginas no encontradas', href: `${base}/404`, icono: 'no-encontradas' },
        // Conectar la web con la Search Console de su dueño (src/google/ en el núcleo): del SEO de toda la web.
        ...(puede('seo.sitio') ? [{ etiqueta: 'Google Search Console', href: `${base}/google`, icono: 'google' as const }] : []),
      ],
    })
  }

  // La estructura de la web (plan del CMS dinámico §5 y §6), con `estructura.ver`: los grupos de campos siempre (valen
  // también para páginas, entradas, productos y servicios); los tipos de contenido y las taxonomías, con el módulo `estructura`.
  // Con la estructura apagada en la instalación (el núcleo lo dice, `api/interruptores.ts`), ni los grupos de campos.
  if (puede('estructura.ver') && interruptores.estructura) {
    const estructura: Enlace[] = []
    if (tiene('estructura')) {
      estructura.push({ etiqueta: 'Tipos de contenido', href: `${base}/tipos-de-contenido`, icono: 'tipos-de-contenido' })
      estructura.push({ etiqueta: 'Taxonomías', href: `${base}/taxonomias`, icono: 'taxonomias' })
    }
    estructura.push({ etiqueta: 'Grupos de campos', href: `${base}/grupos-de-campos`, icono: 'campos' })
    // Proponerla con IA (plan §8.3): con el módulo y con `ia.estructura`.
    if (tiene('estructura') && puede('ia.estructura')) estructura.push({ etiqueta: 'Proponer con IA', href: `${base}/estructura-ia`, icono: 'generar' })
    secciones.push({ etiqueta: 'Estructura', enlaces: estructura })
  }

  // «Generar» solo con `diseno.proponer` (§5), y el historial va dentro: es donde se
  // previsualiza, se compara, se descarta y se publica. El listado técnico de la colección
  // (manifiesto, artefacto, tokens) no le decía nada al cliente. Quien solo ve el diseño mira
  // la historia en solo lectura, porque explica lo que ve en su web.
  if (puede('diseno.proponer')) {
    secciones.push({ etiqueta: 'Diseño', enlaces: [{ etiqueta: 'Generar', href: `${base}/diseno`, icono: 'generar' }] })
  } else if (puede('diseno.ver')) {
    secciones.push({ etiqueta: 'Diseño', enlaces: [{ etiqueta: 'Versiones del diseño', href: `${base}/diseno`, icono: 'versiones' }] })
  }

  if (puede('ajustes.editar')) {
    // El dominio propio se conecta en su pantalla (paginas/Dominio.tsx), que dice qué poner en el DNS y si ya funciona.
    secciones.push({
      etiqueta: 'Sitio',
      enlaces: [
        { etiqueta: 'Ajustes del sitio', href: `${base}/ajustes`, icono: 'ajustes' },
        { etiqueta: 'Dominio', href: `${base}/dominio`, icono: 'dominio' },
        // Otros idiomas de la web (§37): encenderlos abre `/en/…` y `/pt/…` para las versiones que escriba el cliente.
        { etiqueta: 'Idiomas', href: `${base}/idiomas`, icono: 'idiomas' },
      ],
    })
  }

  return secciones
}

/**
 * Las direcciones de antes de que las respuestas de los formularios se llamaran así
 * (`…/s/<sitio>/envios` y `…/envios/<id>`) van en correos ya enviados: llevan a la nueva,
 * con su id y sus filtros. `null` si la dirección no es de esas.
 */
export const direccionNuevaDeRespuestas = (ruta: string, busqueda = ''): null | string => {
  const partes = /^(\/e\/[^/]+(?:\/s\/[^/]+)?)\/envios(?:\/([^/]+))?\/?$/.exec(ruta)
  if (!partes) return null
  return `${partes[1]}/respuestas${partes[2] ? `/${partes[2]}` : ''}${busqueda}`
}

/** La dirección pública de una web, para el botón «Ver la web». */
export const direccionDeLaWeb = (dominio: null | string | undefined, puertoLocal = 4340): null | string => {
  if (!dominio) return null
  const local = dominio.endsWith('.localhost') || dominio === 'localhost'
  return local ? `http://${dominio}:${puertoLocal}` : `https://${dominio}`
}

/**
 * El dominio de la plataforma: toda web se sirve en `<slug>.wixy.app` desde el primer día,
 * tenga o no uno propio (§29.2 de CLAUDE.md). En desarrollo se cambia con
 * `VITE_DOMINIO_PLATAFORMA`; el núcleo usa `DOMINIO_PLATAFORMA` para lo mismo.
 */
export const DOMINIO_DE_LA_PLATAFORMA = String(import.meta.env.VITE_DOMINIO_PLATAFORMA || 'wixy.app').toLowerCase()

/** El dominio por el que se llega a una web: el primero de los suyos o, sin ninguno, el de la plataforma. Nunca `null`. */
export const dominioDe = (sitio: Pick<Sitio, 'direccionDeAgencia' | 'direccionDeAgenciaLista' | 'dominios' | 'slug'>): string =>
  sitio.dominios?.[0]?.dominio || (sitio.direccionDeAgenciaLista && sitio.direccionDeAgencia) || `${sitio.slug}.${DOMINIO_DE_LA_PLATAFORMA}`

export const estadoDelSitio = (
  publicada: null | number | undefined,
  ultima: null | { estado: string } | undefined,
): { clave: 'construyendo' | 'fallida' | 'publicado' | 'sin-publicar'; texto: string } => {
  if (ultima && (ultima.estado === 'en-cola' || ultima.estado === 'construyendo')) return { clave: 'construyendo', texto: 'Construyendo' }
  if (publicada) return { clave: 'publicado', texto: `Publicada la v${publicada}` }
  if (ultima && ultima.estado === 'fallida') return { clave: 'fallida', texto: 'No se pudo construir' }
  return { clave: 'sin-publicar', texto: 'Sin publicar' }
}
